import React, { useEffect, useRef, useState } from 'react';
import NavIcon from '../../common/NavIcon';

// Presentational pieces of the compliance report page (layout per the approved design).
// All data and handlers come from StudentComplianceReport.

export const STATUS_META = {
  non_compliant: {
    label: 'Non-compliant', icon: '✕',
    pill: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
    dot: 'bg-red-600', bar: 'bg-red-600',
  },
  needs_attention: {
    label: 'Needs attention', icon: '!',
    pill: 'bg-[#ffedd5] text-[#9a3412] dark:bg-[#f97316]/15 dark:text-[#fdba74]',
    dot: 'bg-[#ea580c]', bar: 'bg-[#ea580c]',
  },
  compliant: {
    label: 'Compliant', icon: '✓',
    pill: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
    dot: 'bg-blue-600', bar: 'bg-blue-600',
  },
  not_applicable: {
    label: 'Not applicable', icon: '–',
    pill: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    dot: 'bg-slate-400', bar: 'bg-slate-400',
  },
};

export function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.non_compliant;
}

/** "p.34, 99–103" — consecutive pages collapse into ranges. */
export function compactPages(pages, maxGroups = 3) {
  const list = [...new Set((pages || []).map(Number).filter((n) => Number.isInteger(n) && n > 0))].sort((a, b) => a - b);
  if (!list.length) return '';
  const groups = [];
  list.forEach((p) => {
    const last = groups[groups.length - 1];
    if (last && p === last[1] + 1) last[1] = p;
    else groups.push([p, p]);
  });
  const text = groups.slice(0, maxGroups).map(([a, b]) => (a === b ? `${a}` : `${a}–${b}`)).join(', ');
  return `p.${text}${groups.length > maxGroups ? ', …' : ''}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((target - today) / 86400000);
}

export function relativeDays(iso) {
  const n = daysUntil(iso);
  if (n == null) return null;
  if (n < 0) return { text: `${-n} day${n === -1 ? '' : 's'} overdue`, late: true };
  if (n === 0) return { text: 'today', late: true };
  if (n > 540) return { text: `in ${Math.round(n / 365)} yrs`, late: false };
  return { text: `in ${n} day${n === 1 ? '' : 's'}`, late: false };
}

// ── Docked processing stepper ─────────────────────────────────────────────────

const STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'read', label: 'Reading pages' },
  { key: 'extract', label: 'Extracting data' },
  { key: 'check', label: 'Checking compliance' },
  { key: 'save', label: 'Saving' },
];

function currentStep(progress) {
  const phase = String(progress.phase || '').toLowerCase();
  const label = String(progress.phaseLabel || '');
  if (progress.queued || !progress.started) return 0;
  if (progress.awaitingResults || /^(saving|finalize|file_done)$/.test(phase) || /saving/i.test(label)) return 4;
  if (phase === 'compliance' || /compliance|section/i.test(label)) return 3;
  if (/^(extraction|embedding)$/.test(phase) || /extract|embedding|chunk/i.test(label)) return 2;
  if (/^(ocr|ocr_vision|supplement)$/.test(phase) || /read|scanned|ocr|vision|form pages|page \d+/i.test(label)) return 1;
  return 0;
}

function formatRemaining(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  if (s < 90) return 'Under 2 min left';
  const m = Math.round(s / 60);
  if (m < 60) return `About ${m} min left`;
  return `About ${Math.floor(m / 60)} h ${m % 60} min left`;
}

export function ProcessingStepper({ progress, cycleCount, cycleIndex }) {
  if (!progress?.visible) return null;
  const rerunOnly = /rerun/i.test(String(progress.jobType || ''));
  const steps = rerunOnly ? STEPS.filter((s) => s.key === 'check' || s.key === 'save') : STEPS;
  const active = currentStep(progress);
  const activeKey = STEPS[active].key;
  const activeIdx = Math.max(0, steps.findIndex((s) => s.key === activeKey));
  const remaining = formatRemaining(progress.remainingSeconds);
  const title = progress.queued
    ? `Queued${progress.queuePosition ? ` · position ${progress.queuePosition}` : ''}`
    : rerunOnly ? 'Re-running compliance' : 'Processing new upload';

  return (
    <div role="status" aria-live="polite"
      className="bg-indigo-50 border-b border-indigo-200 px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-indigo-950 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-100">
      <span className="font-semibold">{title}</span>
      <ol className="flex flex-wrap gap-1.5" aria-label="Pipeline steps">
        {steps.map((s, i) => {
          const done = i < activeIdx;
          const isActive = i === activeIdx;
          return (
            <li key={s.key}
              aria-current={isActive ? 'step' : undefined}
              className={`rounded-full px-2.5 py-[3px] ${
                done ? 'bg-indigo-200 text-indigo-950 dark:bg-indigo-500/30 dark:text-indigo-100'
                  : isActive ? 'bg-indigo-600 text-white font-semibold'
                    : 'bg-white border border-indigo-200 text-indigo-900/70 dark:bg-transparent dark:border-indigo-500/40 dark:text-indigo-200/70'
              }`}>
              {done && <span aria-hidden="true">✓ </span>}
              {isActive && progress.phaseLabel ? `${s.label} · ${progress.phaseLabel.replace(/^(reading scanned pages|checking compliance)\s*[—-]\s*/i, '')}` : s.label}
              {s.key === 'check' && !isActive && cycleCount > 1 ? ` · ${cycleCount} cycles` : ''}
            </li>
          );
        })}
      </ol>
      <span className="ml-auto text-indigo-700 dark:text-indigo-300">
        {[remaining, cycleCount > 1 ? (cycleIndex ? `cycle ${cycleIndex} of ${cycleCount} · results appear cycle by cycle` : 'results appear cycle by cycle') : null]
          .filter(Boolean).join(' · ')}
      </span>
      <span className="sr-only">{Math.round(progress.percent || 0)}% complete</span>
    </div>
  );
}

// ── Small dropdown menu ───────────────────────────────────────────────────────

export function MenuButton({ label, children, className = '', align = 'right', drop = 'down' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const menuPos = drop === 'up'
    ? 'bottom-full mb-1.5'
    : 'top-full mt-1.5';
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className="w-full h-9 inline-flex items-center justify-center gap-1 rounded-[10px] border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
        {label}
        <NavIcon name="chevronDown" className="w-3.5 h-3.5 text-slate-500" strokeWidth={2} />
      </button>
      {open && (
        <div role="menu" onClick={() => setOpen(false)}
          className={`absolute z-40 ${menuPos} min-w-[13rem] rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:bg-slate-800 dark:border-slate-700 ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {children}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ onClick, disabled, children, hint, icon }) {
  return (
    <button type="button" role="menuitem" disabled={disabled} onClick={onClick}
      className="w-full flex items-center justify-between gap-3 px-3.5 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700">
      <span className="inline-flex items-center gap-2.5 min-w-0">
        {icon ? <span className="flex-shrink-0 text-slate-500 dark:text-slate-400">{icon}</span> : null}
        <span className="truncate">{children}</span>
      </span>
      {hint != null && <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{hint}</span>}
    </button>
  );
}

export const PdfFileIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.75" />
    <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M8.5 16.5c.2-1.6 1.1-2.5 2.7-2.5 1.1 0 1.8.6 1.8 1.5 0 2.1-3.6 2-3.6 3.5h3.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const ExcelFileIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="3.5" width="16" height="17" rx="2" stroke="currentColor" strokeWidth="1.75" />
    <path d="M4 8.5h16M4 13h16M4 17.5h16M9 8.5v12" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

// ── Summary card ──────────────────────────────────────────────────────────────

export function ScoreRing({ score, children }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div
      role="img"
      aria-label={`Compliance score ${pct} percent`}
      className="relative w-[84px] h-[84px] sm:w-[96px] sm:h-[96px] flex-shrink-0"
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{ background: `conic-gradient(#4f46e5 0 ${pct}%, var(--ring-track, #e2e8f0) ${pct}% 100%)` }}
      />
      <div className="absolute inset-[6px] rounded-full overflow-hidden bg-white dark:bg-slate-900 flex items-center justify-center">
        {children || (
          <span className="text-[15px] sm:text-base font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">{pct}%</span>
        )}
      </div>
      <span className="absolute -top-1 -right-1 min-w-[2rem] h-7 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] sm:text-xs font-bold tabular-nums shadow-md flex items-center justify-center ring-2 ring-white dark:ring-slate-900">
        {pct}%
      </span>
    </div>
  );
}

export function KeyDates({ items }) {
  return (
    <ol aria-label="Key dates" className="grid grid-cols-1 sm:grid-cols-3 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden text-[13px]">
      {items.map((it, i) => {
        const rel = relativeDays(it.iso);
        return (
          <li key={it.label} className={`px-3 py-2.5 ${i === 0 ? 'bg-slate-50 dark:bg-slate-800/60' : 'sm:border-l border-t sm:border-t-0 border-slate-200 dark:border-slate-700'}`}>
            <div className="text-xs text-slate-500 dark:text-slate-400">{it.label}</div>
            <b className="text-slate-900 dark:text-white">{it.value}</b>
            {i > 0 && rel && (
              <span className={`ml-1.5 text-xs ${rel.late ? 'font-semibold text-red-700 dark:text-red-400' : i === 1 ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400'}`}>{rel.text}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ── Filter chips ──────────────────────────────────────────────────────────────

export function FilterChips({ value, onChange, counts }) {
  const chips = [
    { key: 'all', label: 'All' },
    { key: 'issues', label: 'Has issues' },
    { key: 'review', label: 'Needs staff review' },
    { key: 'compliant', label: 'Compliant' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label="Filter sections" className="flex flex-wrap gap-1.5">
        {chips.map((c) => {
          const on = value === c.key;
          return (
            <button key={c.key} type="button" aria-pressed={on} onClick={() => onChange(c.key)}
              className={`h-[34px] rounded-full px-3.5 text-[13px] transition-colors ${
                on ? 'bg-slate-900 text-white font-semibold dark:bg-white dark:text-slate-900'
                  : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
              }`}>
              {c.label} {counts[c.key] ?? 0}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────

// Faint status tint over the whole card; the header tile carries the strong colour.
const CARD_TONE = {
  non_compliant: 'bg-red-50/70 border-red-200/80 dark:bg-red-500/[0.06] dark:border-red-500/25',
  needs_attention: 'bg-amber-50/70 border-amber-200/80 dark:bg-amber-500/[0.06] dark:border-amber-500/25',
  compliant: 'bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-800',
  not_applicable: 'bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-800',
};

const STATUS_TILE = {
  non_compliant: 'bg-red-600 text-white',
  needs_attention: 'bg-amber-500 text-white',
  compliant: 'bg-green-600 text-white',
  not_applicable: 'bg-slate-400 text-white',
};

const STATUS_TEXT = {
  non_compliant: 'text-red-700 dark:text-red-400',
  needs_attention: 'text-amber-700 dark:text-amber-400',
  compliant: 'text-green-700 dark:text-green-400',
  not_applicable: 'text-slate-500 dark:text-slate-400',
};

const RING_COLOR = {
  non_compliant: '#dc2626', needs_attention: '#f59e0b', compliant: '#16a34a', not_applicable: '#94a3b8',
};

const BULLET = {
  issue: { mark: '✕', cls: 'text-red-600 dark:text-red-400', text: 'text-red-800 dark:text-red-300' },
  review: { mark: '!', cls: 'text-amber-600 dark:text-amber-400', text: 'text-slate-800 dark:text-slate-200' },
  ok: { mark: '✓', cls: 'text-green-600 dark:text-green-400', text: 'text-slate-700 dark:text-slate-300' },
  data: { mark: '•', cls: 'text-indigo-400', text: 'text-slate-700 dark:text-slate-300' },
};

function CompletionRing({ pct, status }) {
  if (pct == null) return null;
  const r = 18;
  const c = 2 * Math.PI * r;
  return (
    <span className="relative w-12 h-12 flex-shrink-0" title={`${pct}% of required items found`} aria-label={`${pct}% complete`} role="img">
      <svg width="48" height="48" viewBox="0 0 48 48" className="-rotate-90" aria-hidden="true">
        <circle cx="24" cy="24" r={r} fill="none" strokeWidth="3.5" className="stroke-slate-900/10 dark:stroke-white/10" />
        <circle cx="24" cy="24" r={r} fill="none" strokeWidth="3.5" strokeLinecap="round"
          stroke={RING_COLOR[status] || RING_COLOR.non_compliant} strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center px-1 text-[9px] font-bold tabular-nums tracking-tight leading-none text-slate-700 dark:text-slate-200">{pct}%</span>
    </span>
  );
}

function CardBullets({ bullets, fallback }) {
  const items = bullets?.items || [];
  if (!items.length) return <p className="flex-1 text-[13px] text-slate-500 dark:text-slate-400 italic">{fallback}</p>;
  return (
    <ul className="flex-1 flex flex-col gap-1.5 text-[13px] leading-[1.4]">
      {items.map((b, i) => {
        const style = BULLET[b.kind] || BULLET.data;
        return (
          <li key={i} className="flex gap-2 min-w-0">
            <span aria-hidden="true" className={`w-3.5 flex-shrink-0 text-center font-bold ${style.cls}`}>{style.mark}</span>
            <span className={`min-w-0 line-clamp-2 ${style.text}`}>
              {b.label && <b className="font-semibold text-indigo-700 dark:text-indigo-300">{b.label} · </b>}
              {b.text}
            </span>
          </li>
        );
      })}
      {bullets.more > 0 && <li className="pl-[22px] text-xs font-medium text-slate-500 dark:text-slate-400">+{bullets.more} more in this section</li>}
    </ul>
  );
}

const PAGE_LINK_LIMIT = 5;

function SourcePageLinks({ pages, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const list = pages || [];
  if (!list.length) {
    return <span className="text-[11px] text-slate-500 dark:text-slate-400">No source pages</span>;
  }
  const visible = expanded ? list : list.slice(0, PAGE_LINK_LIMIT);
  const more = list.length - visible.length;
  return (
    <span className="flex flex-wrap items-center gap-1 min-w-0" aria-label="Source pages">
      {visible.map((p) => (
        <button key={p} type="button" onClick={() => onOpen(p)}
          className="rounded-md border border-slate-900/10 bg-white/70 px-1.5 text-[11px] font-medium leading-5 text-indigo-700 hover:border-indigo-300 hover:bg-white dark:bg-white/5 dark:border-white/10 dark:text-indigo-300"
          title={`Open page ${p}`}>
          p.{p}
        </button>
      ))}
      {more > 0 && (
        <button type="button" onClick={() => setExpanded(true)}
          className="inline-flex h-5 w-6 items-center justify-center rounded-md border border-slate-900/10 bg-white/70 text-indigo-700 hover:border-indigo-300 hover:bg-white dark:bg-white/5 dark:border-white/10 dark:text-indigo-300"
          title={`Show ${more} more page${more === 1 ? '' : 's'}`}
          aria-label={`Show ${more} more source pages`}>
          <NavIcon name="chevronRight" className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}
    </span>
  );
}

export const SectionSummaryCard = React.memo(function SectionSummaryCard({ entry, onOpen, onReview, onOpenPage }) {
  const meta = statusMeta(entry.status);
  const pct = entry.pct == null ? null : Math.max(0, Math.min(100, entry.pct));
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen(entry.key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(entry.key);
        }
      }}
      className={`group flex flex-col rounded-2xl border shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-md transition-shadow cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${CARD_TONE[entry.status] || CARD_TONE.non_compliant}`}
    >
      <div className="flex items-start gap-3 px-4 pt-4 pb-3 text-left rounded-t-2xl">
        <span aria-hidden="true" className={`w-9 h-9 rounded-xl flex items-center justify-center text-[15px] font-bold flex-shrink-0 ${STATUS_TILE[entry.status] || STATUS_TILE.non_compliant}`}>
          {entry.isRunning
            ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
            : meta.icon}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold leading-snug text-slate-900 dark:text-white">{entry.label}</span>
          <span className="block text-xs mt-0.5">
            <span className={`font-semibold ${STATUS_TEXT[entry.status] || STATUS_TEXT.non_compliant}`}>
              {entry.isRunning ? `Re-running${entry.runningLabel ? ` ${entry.runningLabel}` : ''}` : meta.label}
            </span>
            {entry.subtitle && <span className="text-slate-500 dark:text-slate-400"> · {entry.subtitle}</span>}
          </span>
        </span>
        <CompletionRing pct={pct} status={entry.status} />
      </div>

      <div className="flex flex-wrap gap-1.5 px-4">
        <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold ${
          entry.issues ? 'bg-red-600/10 text-red-800 dark:bg-red-500/15 dark:text-red-300' : 'bg-slate-900/5 text-slate-600 dark:bg-white/5 dark:text-slate-300'
        }`}>
          {entry.issues} issue{entry.issues === 1 ? '' : 's'}
        </span>
        {entry.review > 0 ? (
          <button type="button" onClick={(e) => { e.stopPropagation(); onReview(entry.key); }}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold bg-amber-500/15 text-amber-900 hover:bg-amber-500/25 dark:text-amber-300"
            title="Open the review items for this section">
            {entry.review} to review →
          </button>
        ) : (
          <span className="inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-semibold bg-slate-900/5 text-slate-600 dark:bg-white/5 dark:text-slate-300">Nothing to review</span>
        )}
      </div>

      <div className="mx-4 my-3 border-t border-slate-900/[0.07] dark:border-white/[0.08]" />

      <div className="px-4 flex-1 flex flex-col min-h-[96px]">
        <CardBullets bullets={entry.bullets} fallback={entry.top} />
      </div>

      <div className="flex items-center gap-1.5 px-4 pb-4 pt-3 mt-1" onClick={(e) => e.stopPropagation()}>
        <SourcePageLinks pages={entry.pageList} onOpen={(p) => onOpenPage(entry, p)} />
      </div>
    </article>
  );
});

// ── Activity timeline (right status pane) ─────────────────────────────────────

const TONE_DOT = {
  processing: 'bg-indigo-500 ring-4 ring-indigo-500/20 animate-pulse',
  failed: 'bg-red-600 ring-4 ring-red-600/15',
  warning: 'bg-amber-500 ring-4 ring-amber-500/20',
  ok: 'bg-green-600 ring-4 ring-green-600/15',
  info: 'bg-slate-400 ring-4 ring-slate-400/15',
};

export function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.round(s / 86400)} d ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ActivityTimeline({ items, hasMore, onLoadMore, notice, title = 'Activity' }) {
  return (
    <section aria-label={title} className="flex flex-col min-h-0">
      <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
        <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white">{title}</h2>
        <span className="text-xs text-slate-500 dark:text-slate-400">{items.length ? 'Newest first' : ''}</span>
      </div>
      {notice}
      {items.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">No activity recorded yet.</p>
      ) : (
        <ol className="relative px-4 pb-4">
          <span className="absolute left-[21px] top-2 bottom-6 w-px bg-slate-200 dark:bg-slate-700" aria-hidden="true" />
          {items.map((it) => (
            <li key={it.id} className="relative flex gap-3 pb-4 last:pb-0">
              <span className={`relative z-10 mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${TONE_DOT[it.tone] || TONE_DOT.info}`} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-white">{it.title}</p>
                  {it.badge && <span className={`flex-shrink-0 rounded-full px-2 py-px text-[10.5px] font-semibold ${it.badge.style}`}>{it.badge.label}</span>}
                </div>
                {it.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 break-words">{it.description}</p>}
                {it.extra}
                <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1" title={it.fullTime}>{it.when}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
      {hasMore && (
        <div className="px-4 pb-4">
          <button type="button" onClick={onLoadMore}
            className="w-full h-9 rounded-[10px] border border-slate-200 text-[13px] font-medium text-indigo-700 hover:bg-slate-50 dark:border-slate-700 dark:text-indigo-300 dark:hover:bg-slate-800">
            Load older activity
          </button>
        </div>
      )}
    </section>
  );
}

// ── Left panel pieces ─────────────────────────────────────────────────────────

export function PanelLabel({ children, className = '' }) {
  return <div className={`px-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500 dark:text-slate-400 ${className}`}>{children}</div>;
}

export function SectionNavItem({ entry, active, onOpen, onOpenPage }) {
  const meta = statusMeta(entry.status);
  return (
    <div className={`rounded-lg ${active ? 'bg-indigo-50 dark:bg-indigo-500/15' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
      <button type="button" onClick={() => onOpen(entry.key)} aria-current={active ? 'page' : undefined}
        className="w-full flex items-center gap-2.5 px-2 pt-[7px] pb-0.5 text-left text-sm">
        <span aria-hidden="true" className={`w-[18px] h-[18px] rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 ${meta.dot}`}>{meta.icon}</span>
        <span className={`flex-1 min-w-0 leading-snug break-words ${active ? 'font-semibold text-indigo-900 dark:text-indigo-100' : 'text-slate-700 dark:text-slate-300'}`}>{entry.label}</span>
        {entry.review > 0 ? (
          <span className="rounded-full bg-[#ffedd5] px-1.5 text-xs font-semibold text-[#9a3412] dark:bg-[#f97316]/15 dark:text-[#fdba74]" title={`${entry.review} to review`}>{entry.review}</span>
        ) : entry.issues > 0 ? (
          <span className="text-xs font-semibold text-red-700 dark:text-red-400" title={`${entry.issues} issues`}>{entry.issues}</span>
        ) : null}
      </button>
      <div className="pl-[36px] pr-2 pb-[7px]">
        {entry.pagesText ? (
          <button type="button" onClick={() => onOpenPage(entry)} className="text-[11px] text-indigo-600 hover:underline dark:text-indigo-400 break-words text-left"
            title={`Open source page ${entry.pageList[0]}`}>
            {entry.pagesText}
          </button>
        ) : <span className="text-[11px] text-slate-400 dark:text-slate-500">No source pages</span>}
      </div>
    </div>
  );
}
